// src/components/ProfileMenu.js

import React, { useState, useEffect, useCallback } from "react";
import {
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AvatarWithStatus from "../AvatarWithStatus";

// Иконки MUI (пример — подберите по желанию)
import WorkspacesIcon from "@mui/icons-material/Workspaces"; // для Workflow
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline"; // для Messages
import ApiIcon from "@mui/icons-material/Api"; // для API
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"; // для Billing
import SettingsIcon from "@mui/icons-material/Settings"; // для My profile
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings"; // для Settings (admin)
import LogoutIcon from "@mui/icons-material/Logout"; // для Logout
import StarIcon from "@mui/icons-material/Star";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { ROLE_USER, isAdminRole, normalizeRole } from "../../utils/userRoles";
import { resolvePlanLimit } from "../../config/billingPlans";
import { acme, acmeMenuPaperSx } from "../../theme/acmeTheme";

function formatSmsCredits(value) {
  return Math.max(0, Math.floor(Number(value) || 0)).toLocaleString();
}

function normalizeSmsWallet(data) {
  if (!data) return null;
  const credits = Math.max(
    0,
    Math.floor(Number(data.smsCredits ?? data.credits) || 0),
  );
  const peak = Math.max(
    credits,
    Math.floor(Number(data.smsCreditsPeak ?? data.creditsPeak) || 0),
  );
  return {
    credits,
    peak,
    unlimitedSms: !!data.unlimitedSms,
    planName: data.planName || "",
  };
}

const planRowSx = {
  display: "flex",
  alignItems: "center",
};

const planCaptionSx = {
  display: "block",
  mt: 0.5,
  color: acme.colors.mutedForegroundSolid,
  fontSize: "0.75rem",
  lineHeight: 1.4,
};

const ProfileMenu = ({ user, handleLogout, navTone = "dark" }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [smsWallet, setSmsWallet] = useState(null);
  const [smsLoading, setSmsLoading] = useState(false);
  const open = Boolean(anchorEl);

  const loadSmsWallet = useCallback(async () => {
    if (isAdminRole(user?.role)) return;
    setSmsLoading(true);
    try {
      const { data } = await axios.get("/api/sms-campaigns/credits");
      setSmsWallet(normalizeSmsWallet(data));
    } catch {
      setSmsWallet(null);
    } finally {
      setSmsLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadSmsWallet();
  }, [loadSmsWallet]);

  useEffect(() => {
    if (open) {
      loadSmsWallet();
    }
  }, [open, loadSmsWallet]);

  // Если данные о пользователе ещё не загрузились
  if (!user) {
    return <CircularProgress size={24} color="inherit" />;
  }

  const handleClick = (event) => {
    // Сохраняем ссылку на кнопку, которая вызвала меню
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Список пунктов меню с иконками
  const menuItems = [
    { text: "Workflow", path: "/dashboard", Icon: WorkspacesIcon },
    // Messages доступны только для admin, manager и support, но НЕ для user
    ...(normalizeRole(user.role) !== ROLE_USER
      ? [{ text: "Messages", path: "/messages", Icon: ChatBubbleOutlineIcon }]
      : []),
    ...(isAdminRole(user.role)
      ? [
          { text: "API", path: "/api-settings", Icon: ApiIcon },
          { text: "Settings", path: "/settings", Icon: AdminPanelSettingsIcon },
        ]
      : []),
    {
      text: "Billing",
      path: "/billing",
      Icon: ReceiptLongIcon,
      color: "orange",
    },
    { text: "My profile", path: "/myprofile", Icon: SettingsIcon },
  ];

  // === ДОБАВЛЯЕМ УСЛОВИЕ ===
  if (isAdminRole(user.role)) {
    menuItems.push({
      text: "Management",
      path: "/admin",
      Icon: WorkspacesIcon, // или любая другая иконка на твой вкус
    });
  }

  // Получаем ширину кнопки для правильного позиционирования меню
  const buttonWidth = anchorEl?.offsetWidth || 260;

  const isLightNav = navTone === "light";

  return (
    <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
      {/* Кнопка с аватаром и именем пользователя */}
      <IconButton
        id="profile-menu-button"
        onClick={handleClick}
        disableRipple
        sx={{
          backgroundColor: isLightNav
            ? acme.colors.muted
            : "rgba(255,255,255,0.1)",
          borderRadius: acme.radius.md,
          color: isLightNav ? acme.colors.foreground : "#fff",
          padding: "5px 10px",
          width: "100%",
          justifyContent: "flex-start",
          py: 1,
          boxShadow: "none",

          border: "none",
          "&:hover": {
            backgroundColor: isLightNav
              ? acme.colors.muted
              : "rgba(255,255,255,0.1)",
          },
        }}
      >
        <AvatarWithStatus user={user} />
        <Box
          sx={{
            ml: 1,
            textAlign: "left",
            justifyContent: "center",
            maxWidth: "calc(100% - 60px)",
          }}
        >
          <Typography
            variant="body2"
            fontWeight="bold"
            sx={{
              lineHeight: "1.2",
              mt: 0.5,
              mb: 0.5,
              color: isLightNav ? acme.colors.foreground : "#fff",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.login}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              lineHeight: "1.2",
              color: isLightNav ? acme.colors.mutedForegroundSolid : "#fff",
              opacity: isLightNav ? 1 : 0.8,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
            }}
          >
            {user.email}
          </Typography>
        </Box>
      </IconButton>

      {/* Меню профиля */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            ...acmeMenuPaperSx,
            mt: 1,
            width: buttonWidth,
            maxWidth: "100%",
            overflow: "visible",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: -10,
              left: "50%",
              transform: "translateX(-50%) rotate(45deg)",
              width: 20,
              height: 20,
              bgcolor: acme.colors.background,
              borderLeft: `1px solid ${acme.colors.border}`,
              borderTop: `1px solid ${acme.colors.border}`,
              zIndex: 0,
            },
          },
        }}
      >
        {/* Заголовок с информацией о пользователе */}
        <Box
          sx={{
            p: 2,
            pb: 1.5,
            borderBottom: `1px solid ${acme.colors.border}`,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{
              color: acme.colors.foreground,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.firstName || user.login}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user.email}
          </Typography>
        </Box>

        {/* Email + SMS plan info (non-admin) */}
        {!isAdminRole(user.role) && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${acme.colors.border}`,
            }}
          >
            <Box sx={{ ...planRowSx, mb: 1.5 }}>
              <StarIcon
                fontSize="small"
                sx={{ color: acme.colors.mutedForegroundSolid, mr: 1 }}
              />
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ color: acme.colors.foreground }}
              >
                {user.plan || user.planName || "Basic"} Plan
              </Typography>
            </Box>
            <Typography variant="caption" sx={planCaptionSx}>
              {user.usedEmails || 0}/
              {resolvePlanLimit(
                user.planName || user.plan,
                user.planLimit ?? user.emailLimit,
              )}{" "}
              emails used
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={planRowSx}>
              <SmsOutlinedIcon
                fontSize="small"
                sx={{ color: acme.colors.mutedForegroundSolid, mr: 1 }}
              />
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{ color: acme.colors.foreground }}
              >
                SMS Marketing
                {smsWallet?.unlimitedSms && smsWallet.planName
                  ? `: ${smsWallet.planName}`
                  : ""}
              </Typography>
            </Box>
            {smsLoading && !smsWallet ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <CircularProgress size={12} sx={{ color: acme.colors.brand }} />
                <Typography variant="caption" sx={planCaptionSx}>
                  Loading SMS balance…
                </Typography>
              </Box>
            ) : smsWallet ? (
              <Typography variant="caption" sx={planCaptionSx}>
                {smsWallet.unlimitedSms
                  ? "Unlimited SMS sending"
                  : `${formatSmsCredits(smsWallet.credits)} / ${formatSmsCredits(smsWallet.peak)} credits remaining`}
              </Typography>
            ) : (
              <Typography variant="caption" sx={planCaptionSx}>
                SMS credits unavailable
              </Typography>
            )}
          </Box>
        )}

        {/* Пункты меню из массива menuItems */}
        <Box sx={{ py: 1 }}>
          {menuItems.map((item) => (
            <MenuItem
              key={item.text}
              onClick={() => {
                handleClose();
                navigate(item.path);
              }}
              sx={{
                py: 1,
                px: 2,
                borderRadius: acme.radius.sm,
                mx: 0.5,
                "&:hover": {
                  backgroundColor: acme.colors.muted,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: item.color || acme.colors.mutedForegroundSolid,
                  minWidth: "36px",
                }}
              >
                <item.Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  sx: {
                    color: item.color || "inherit",
                    fontWeight: 500,
                  },
                }}
              />
            </MenuItem>
          ))}
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {/* Отдельный пункт для Logout (выход) */}
        <MenuItem
          onClick={() => {
            handleClose();
            handleLogout();
          }}
          sx={{
            color: "error.main",
            py: 1,
            px: 2,
            mb: 0.5,
            "&:hover": {
              backgroundColor: "rgba(211, 47, 47, 0.08)",
            },
          }}
        >
          <ListItemIcon sx={{ color: "error.main", minWidth: "36px" }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              sx: {
                color: "error.main",
                fontWeight: 500,
              },
            }}
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ProfileMenu;
